import { useCallback, useMemo } from 'react';

import type { DisplayCreative, IDimensionDefinition } from '@feathr/blackbox';
import { ValidDimensions, ValidFacebookDimensions } from '@feathr/blackbox';

import type { TChannel } from '../types';
import type { IAdDimensionOption } from './AdDimensionsSelect.types';
import { buildDimensionOptions } from './AdDimensionsSelect.utils';

interface IUseAdDimensionsSelectParams {
  /** Optional channel to filter dimensions by. */
  channel?: TChannel;
  /** Creative model for uncontrolled mode. */
  creative?: DisplayCreative;
  /** Custom dimensions to use instead of defaults. */
  dimensions?: Record<string, IDimensionDefinition>;
  /** Callback for controlled mode. */
  onChange?: (spec: string) => void;
  /** Current value for controlled mode. */
  value?: string;
}

interface IUseAdDimensionsSelectReturn {
  /** Available dimension options for the select. */
  dimensionOptions: IAdDimensionOption[];
  /** Handler for when a dimension is selected. */
  handleSelectDimensions: (option: IAdDimensionOption) => void;
  /** Currently selected dimension option, or null if none selected. */
  selectedValue: IAdDimensionOption | null;
}

/**
 * Hook that manages dimension selection state and options.
 *
 * Supports two modes:
 * - Controlled: Pass `value` and `onChange` to manage state externally
 * - Uncontrolled: Pass `creative` to update the model directly
 *
 * @param params - Configuration for the hook
 * @returns Dimension options, selected value, and selection handler
 *
 * @example
 * // Controlled mode
 * const { dimensionOptions, selectedValue, handleSelectDimensions } = useAdDimensionsSelect({
 *   channel: 'display',
 *   value: 'medium_rectangle',
 *   onChange: (spec) => setSpec(spec),
 * });
 *
 * @example
 * // Uncontrolled mode with creative
 * const { dimensionOptions, selectedValue, handleSelectDimensions } = useAdDimensionsSelect({
 *   creative: myCreative,
 * });
 */
export function useAdDimensionsSelect({
  channel,
  creative,
  dimensions: dimensionsProp,
  onChange,
  value: valueProp,
}: IUseAdDimensionsSelectParams): IUseAdDimensionsSelectReturn {
  const dimensionOptions = useMemo(() => {
    if (dimensionsProp) {
      return buildDimensionOptions(dimensionsProp, false);
    }

    if (channel === 'display') {
      return buildDimensionOptions(ValidDimensions, false);
    }

    if (channel === 'meta') {
      return buildDimensionOptions(ValidFacebookDimensions, true);
    }

    return [
      ...buildDimensionOptions(ValidDimensions, false),
      ...buildDimensionOptions(ValidFacebookDimensions, true),
    ];
  }, [channel, dimensionsProp]);

  const currentSpec = creative ? creative.get('spec') : valueProp;
  const selectedValue = dimensionOptions.find((dim) => dim.id === currentSpec) ?? null;

  const handleSelectDimensions = useCallback(
    (option: IAdDimensionOption): void => {
      if (creative) {
        creative.set({
          height: option.height,
          spec: option.id,
          width: option.width,
        });
      } else if (onChange) {
        onChange(option.id);
      }
    },
    [creative, onChange],
  );

  return {
    dimensionOptions,
    handleSelectDimensions,
    selectedValue,
  };
}
